import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Loader2, Check, AlertCircle, Shield } from 'lucide-react';
import axios from 'axios';
import { useAuth, formatApiError } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const { user, getAuthHeader, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Client-side validation matching backend rules
  const validateClientSide = () => {
    if (!current) return 'Informe a senha atual';
    if (!next) return 'Informe a nova senha';
    if (next.length < 8) return 'A nova senha deve ter pelo menos 8 caracteres';
    if (next !== confirm) return 'A confirmação não confere com a nova senha';
    if (next === current) return 'A nova senha deve ser diferente da atual';
    const hasLetter = /[a-zA-Z]/.test(next);
    const hasDigit = /[0-9]/.test(next);
    if (!hasLetter || !hasDigit) return 'A nova senha deve conter letras e números';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const clientErr = validateClientSide();
    if (clientErr) {
      setError(clientErr);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next, confirm_password: confirm },
        { headers: getAuthHeader(), withCredentials: true }
      );
      // Update token with the new one returned (current session keeps working)
      if (data?.token) {
        localStorage.setItem('dt_admin_token', data.token);
      }
      setSuccess('Senha alterada com sucesso! Por segurança, você será desconectado em 3 segundos.');
      setCurrent('');
      setNext('');
      setConfirm('');
      // Force re-login after 3s for extra safety
      setTimeout(async () => {
        await logout();
        navigate('/admin/login');
      }, 3000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Erro ao trocar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Configurações</h1>
        <p className="text-[#94A3B8]">Gerencie sua conta de administrador</p>
      </div>

      {/* Account info */}
      <div className="bg-[#0d0f17] border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="text-[#22D3EE]" size={20} />
          <h2 className="text-lg font-semibold text-[#F8FAFC]">Sua Conta</h2>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-[#94A3B8]">Email</span>
            <span className="text-[#F8FAFC]" data-testid="settings-email">{user?.email}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-[#94A3B8]">Papel</span>
            <span className="text-[#22D3EE]">{user?.role || 'admin'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-[#94A3B8]">Nome</span>
            <span className="text-[#F8FAFC]">{user?.name}</span>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="bg-[#0d0f17] border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <Lock className="text-[#22D3EE]" size={20} />
          <h2 className="text-lg font-semibold text-[#F8FAFC]">Trocar Senha</h2>
        </div>
        <p className="text-xs text-[#94A3B8] mb-5">
          Após a troca, a senha antiga deixa de funcionar e todos os outros dispositivos serão desconectados automaticamente.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
          {/* Current password */}
          <div>
            <label className="block text-sm font-medium text-[#94A3B8] mb-2">Senha Atual</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
              <input
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                data-testid="current-password-input"
                className="w-full bg-[#080710] border border-white/10 rounded-lg pl-10 pr-10 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                placeholder="Sua senha atual"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#22D3EE]"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* New password */}
          <div>
            <label className="block text-sm font-medium text-[#94A3B8] mb-2">Nova Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
              <input
                type={showNext ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={8}
                data-testid="new-password-input"
                className="w-full bg-[#080710] border border-white/10 rounded-lg pl-10 pr-10 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                placeholder="Mínimo 8 caracteres, letras e números"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNext(!showNext)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#22D3EE]"
                tabIndex={-1}
              >
                {showNext ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {next && (
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className={next.length >= 8 ? 'text-emerald-400' : 'text-[#94A3B8]'}>
                  {next.length >= 8 ? '✓' : '○'} 8+ caracteres
                </span>
                <span className={/[a-zA-Z]/.test(next) ? 'text-emerald-400' : 'text-[#94A3B8]'}>
                  {/[a-zA-Z]/.test(next) ? '✓' : '○'} Letras
                </span>
                <span className={/[0-9]/.test(next) ? 'text-emerald-400' : 'text-[#94A3B8]'}>
                  {/[0-9]/.test(next) ? '✓' : '○'} Números
                </span>
              </div>
            )}
          </div>

          {/* Confirm new password */}
          <div>
            <label className="block text-sm font-medium text-[#94A3B8] mb-2">Confirmar Nova Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="confirm-password-input"
                className="w-full bg-[#080710] border border-white/10 rounded-lg pl-10 pr-10 py-2.5 text-[#F8FAFC] focus:border-[#22D3EE] outline-none"
                placeholder="Digite a nova senha novamente"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#22D3EE]"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirm && next && confirm !== next && (
              <p className="mt-1 text-xs text-red-400">As senhas não conferem</p>
            )}
          </div>

          {/* Error / Success */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
              data-testid="change-password-error"
              className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 text-sm flex items-start gap-2"
            >
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
              data-testid="change-password-success"
              className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg px-4 py-3 text-sm flex items-start gap-2"
            >
              <Check size={16} className="flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </motion.div>
          )}

          <button
            type="submit"
            disabled={loading || !!success}
            data-testid="change-password-submit"
            className="w-full bg-[#22D3EE] text-[#080710] py-3 rounded-lg font-semibold hover:shadow-[0_0_30px_rgba(34,211,238,0.4)] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />}
            {loading ? 'Alterando...' : 'Alterar Senha'}
          </button>
        </form>
      </div>
    </div>
  );
}
